import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {
  CardElement,
  useElements,
  useStripe,
  CardCvcElement,
  AddressElement,
} from "@stripe/react-stripe-js";
import { get, isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import SavedCards from "./SavedCards";
import MDButton from "components/MDButton";
import {
  postCardPayment,
  updateSubscriptionAfter3dSecure,
} from "store/slice/payment/paymentSlice";
import { amountToShow } from "global/functions";

const Card = (props) => {
  const { subscriptionData, formikProps } = props;

  const [errorAlert, setErrorAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cvcError, setCvcError] = useState(false);

  const cardDetails = useSelector((state) => state.payment.cardDetails);
  const [show, setShow] = useState(!isEmpty(cardDetails));

  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();

  const subscriptionId = get(subscriptionData, "_id", "");

  const subscriptionContract = get(
    subscriptionData,
    "placeId.subscriptionContract",
    "",
  );

  const customer = get(subscriptionData, "customerId", {});
  const customerId = get(customer, "_id", "");

  const customerName = `${get(customer, "firstName", "")} ${get(
    customer,
    "lastName",
    "",
  )}`.trim();

  const customerEmail = get(customer, "email", "");
  const customerPhone = get(customer, "secondaryMobile", "");

  const totalPayAmount = subscriptionData.isMonthly
    ? get(subscriptionData, "firstMonthTotalAmount", 0)
    : get(subscriptionData, "totalAmount", 0);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handlePayment = (stripe, elements) => async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const isInvalid = get(cardElement, "_invalid", false);
    const isEmpty = get(cardElement, "_empty", false);
    const isValid = !(isEmpty || isInvalid);

    const postPayment = async ({
      cardId,
      saveLater,
      cvcTokenId = null,
      address = {},
    }) => {
      try {
        const payload = {
          subscriptionId,
          subscriptionContract,
          customerId,
          paymentMethodId: cardId,
          saveCard: saveLater,
          cvcToken: cvcTokenId,
          address,
        };

        const result = await dispatch(postCardPayment(payload)).unwrap();

        if (result.success) {
          if (result.isAlreadyPaid) {
            setLoading(false);

            navigate("payment-result", {
              state: {
                status: "alreadyPaid",
                data: result.data,
              },
            });

            return;
          }

          if (result.requiresAction) {
            const { error, paymentIntent } = await stripe.confirmCardPayment(
              result.clientSecret,
            );

            const securePayload = {
              securePaymentDataId: result.securePaymentDataId,
              paymentIntent,
              error: error?.message,
            };

            const secureResult = await dispatch(
              updateSubscriptionAfter3dSecure(securePayload),
            ).unwrap();

            setLoading(false);

            navigate("payment-result", {
              state: {
                status: secureResult.success ? "paid" : "failed",
                data: secureResult.data,
              },
            });
          } else {
            setLoading(false);

            navigate("payment-result", {
              state: {
                status: "paid",
                data: result.data,
              },
            });
          }
        } else {
          setLoading(false);

          navigate("payment-result", {
            state: {
              status: "failed",
              data: result,
            },
          });
        }
      } catch (error) {
        setLoading(false);
        console.error("post payment error =====>", error);
      }
    };

    if (!show) {
      if (isValid) {
        setErrorAlert(false);
        setLoading(true);

        const addressElement = elements.getElement(AddressElement);

        const { complete, value } = await addressElement.getValue();

        if (!complete) {
          setErrorAlert(true);
          setLoading(false);
          return;
        }

        const billingAddress = value.address;

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: customerName,
            email: customerEmail,
            phone: customerPhone,
            address: billingAddress,
          },
        });

        if (error) {
          setErrorAlert(true);
          setLoading(false);
          console.log("error ===>", error);
        } else {
          postPayment({
            cardId: get(paymentMethod, "id", ""),
            saveLater: get(formikProps, "values.saveLater", false),
            address: billingAddress,
          });
        }
      } else {
        setErrorAlert(true);
      }
    } else {
      setLoading(true);

      const cvcElement = elements.getElement(CardCvcElement);
      let cvcTokenId = null;

      if (cvcElement) {
        const { token, error } = await stripe.createToken(
          "cvc_update",
          cvcElement,
        );

        if (error) {
          console.error("CVC token error ============>", error);
          setLoading(false);
          setCvcError(true);
          return;
        }

        cvcTokenId = token?.id || null;
        setCvcError(false);
      }

      const addressElement = elements.getElement(AddressElement);

      const { complete, value } = await addressElement.getValue();

      if (!complete) {
        setErrorAlert(true);
        setLoading(false);
        return;
      }

      const billingAddress = value.address;

      postPayment({
        cardId: get(formikProps, "values.selectedCard", ""),
        saveLater: get(formikProps, "values.saveLater", false),
        cvcTokenId,
        address: billingAddress,
      });
    }
  };

  const CARD_ELEMENT_OPTIONS = {
    iconStyle: "solid",
    hidePostalCode: false,
    style: {
      base: {
        iconColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
        fontSize: "18px",
        fontWeight: 500,
        fontSmoothing: "antialiased",
        ":-webkit-autofill": {
          color: "#fce883",
        },
        "::placeholder": {
          color: theme.palette.grey[400],
        },
      },
      invalid: {
        color: theme.palette.error.main,
        ":focus": {
          color: theme.palette.error.light,
        },
      },
    },
  };

  return (
    <>
      {show ? (
        <>
          <SavedCards
            customerId={customerId}
            formikProps={formikProps}
            setShow={setShow}
          />

          {/* Ask for CVC when charging a saved card */}
          <div style={{ margin: "1rem 0" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
              }}
            >
              <AddressElement
                options={{
                  mode: "billing",
                  autocomplete: {
                    mode: "automatic",
                  },
                  fields: {
                    phone: "never",
                  },
                  defaultValues: {
                    name: customerName,
                  },
                }}
              />
            </Paper>
            <Typography variant="subtitle2" className="mb-1">
              Enter CVC Code
            </Typography>
            <Paper variant="outlined" sx={{ py: 1, px: 2 }}>
              {cvcError && (
                <Typography variant="caption" color="error">
                  Incorrect CVC!
                </Typography>
              )}
              <CardCvcElement />
            </Paper>
          </div>

          <Typography
            variant="subtitle1"
            color="secondary"
            className="my-3 text-center cursor-pointer"
            onClick={() => setShow(!show)}
            fontSize="16px"
          >
            + Add a new Card
          </Typography>
        </>
      ) : (
        <>
          {errorAlert && (
            <Alert
              severity="error"
              className="mb-2"
              onClose={() => setErrorAlert(false)}
            >
              Please First Fill Your Card Details
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
            }}
          >
            <AddressElement
              options={{
                mode: "billing",
                autocomplete: {
                  mode: "automatic",
                },
                fields: {
                  phone: customerPhone ? "never" : "always",
                },
                validation: customerPhone
                  ? undefined
                  : {
                      phone: {
                        required: "always",
                      },
                    },
                defaultValues: {
                  name: customerName,
                  address: {
                    country: "US",
                  },
                },
              }}
            />
          </Paper>

          <Paper
            variant="outlined"
            className="px-2"
            sx={{
              py: 1.3,
              width: "100%",
            }}
          >
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </Paper>

          <FormGroup
            className="my-1"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="saveLater"
                  checked={formikProps.values.saveLater}
                  onChange={formikProps.handleChange}
                  disabled={subscriptionData.isAutoRenew}
                />
              }
              label="Save Card"
            />
          </FormGroup>
        </>
      )}

      <Box className="text-center">
        <MDButton
          variant="contained"
          color={sidenavColor}
          onClick={handlePayment(stripe, elements)}
          disabled={loading}
          sx={{
            width: {
              xs: "150px",
              sm: "250px",
            },
          }}
        >
          {`Pay $${amountToShow(totalPayAmount)}`}
        </MDButton>
      </Box>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <Stack direction="column" spacing={1} alignItems="center">
          <CircularProgress color="inherit" />

          <Typography
            variant="h5"
            sx={{
              color: "primary.contrastText",
            }}
            className="mt-4"
          >
            Please wait payment is processing...
          </Typography>
        </Stack>
      </Backdrop>
    </>
  );
};

export default Card;
